import { AnimatePresence, motion } from 'motion/react';
import { KeyboardEvent, PropsWithChildren, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import AccountMenuContent from '@/components/app-shell/AccountMenuContent';
import ShellAccountMenuTrigger from '@/components/app-shell/ShellAccountMenuTrigger';
import { Dropdown } from '@/components/ui/daisy/dropdown';
import { useDropdownClickOutside } from '@/components/ui/daisy/dropdown/useDropdownClickOutside';
import useToggle from '@/data/hook/useToggle';
import { expoOut, swapHorizontal } from '@/utils/animations/commom';

type AccountPopoverProps = PropsWithChildren<{
  className?: string;
  onNavigate?(): void;
}>;

export default function AccountPopover({
  className,
  onNavigate,
  children,
}: AccountPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { isOpen, toggle, close } = useToggle();
  const { t } = useTranslation('translation', {
    keyPrefix: 'appShell.account',
  });

  useDropdownClickOutside({ panelRef, active: isOpen, onClickOutside: close });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') close();
  };

  const handleNavigate = () => {
    close();
    onNavigate?.();
  };

  return (
    <Dropdown
      open
      position="right"
      align="end"
      className="flex w-full justify-center"
      onKeyDown={handleKeyDown}
    >
      <ShellAccountMenuTrigger
        isOpen={isOpen}
        onClick={toggle}
        className={className}
      >
        {children}
      </ShellAccountMenuTrigger>
      <AnimatePresence>
        {isOpen && (
          <Dropdown.Content
            ref={panelRef}
            role="dialog"
            aria-label={t('label')}
            className="z-30 ml-6 w-76"
          >
            <motion.div
              {...swapHorizontal}
              transition={expoOut}
              className="rounded-2xl bg-base-200 p-4 shadow-sm"
            >
              <AccountMenuContent onNavigate={handleNavigate} />
            </motion.div>
          </Dropdown.Content>
        )}
      </AnimatePresence>
    </Dropdown>
  );
}
